import React, { useEffect, memo, useState, useContext } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { ObjectShape } from "yup/lib/object";

import { Color } from "../../../../../../../components/Form/Button";
import { GroupedButtons } from "../../../../../../../components/Generic/GroupedButtons";
import { Checklist } from "../../../../../../../models/checklist";
import GeneralTab from "./General";
import { ButtonsContainer } from "./General/styles";
import { ChecklistContext } from "./context";
import { QuestionTypes } from "../utils";

interface Props {
  checklistToUpdate?: Checklist;
  hasError?: boolean;
  open: boolean;
  isOnViewMode: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submit: (checklist?: Checklist) => void;
}

const ChecklistModal = ({
  checklistToUpdate,
  hasError,
  isOnViewMode,
  isLoading,
  closeModal,
  submit,
}: Props) => {
  const { t: translate } = useTranslation();

  const { checklist, handleUpdateChecklist, handleClearFields, setErrors } =
    useContext(ChecklistContext);

  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const questionSchema: any = yup.lazy(() =>
    yup.object().shape({
      description: yup.string(),
      mandatory: yup.boolean(),
      questionOrder: yup.number(),
      title: yup.string().required(translate("thisFieldIsRequired")),
      type: yup.string().required(translate("thisFieldIsRequired")),
      options: yup.array().when("type", {
        is: (type: string) => type === QuestionTypes.SINGLE || type === QuestionTypes.MULTIPLE,
        then: yup
          .array()
          .min(1, translate("atLeastOneOptionIsRequired"))
          .of(
            yup.object().shape({
              name: yup.string().required(translate("thisFieldIsRequired")),
              valid: yup.boolean(),
            })
          )
          .required(translate("thisFieldIsRequired"))
          .test("valid-options-count", function (value: any) {
            const { type } = this.parent;
            const validOptions =
              (value || [])?.filter((option: any) => option.valid === true) || [];

            if (type === QuestionTypes.MULTIPLE) {
              if (validOptions.length < 1) {
                return this.createError({
                  message: translate("atLeastOneOptionIsRequired"),
                  path: this.path,
                });
              }
            } else if (type === QuestionTypes.SINGLE) {
              if (validOptions.length < 2) {
                return this.createError({
                  message: translate("atLeastTwoOptionsAreRequired"),
                  path: this.path,
                });
              }
            }

            return true;
          }),
      }),
      questionLogics: yup.array().of(questionLogicSchema as yup.AnySchema),
    })
  );

  const questionLogicSchema: any = yup.lazy(() =>
    yup.object().shape({
      logicCondition: yup.string().required(translate("thisFieldIsRequired")),
      logicValue: yup.string().required(translate("thisFieldIsRequired")),
      isEvidenceNotesRequired: yup.boolean(),
      isEvidencePhotosRequired: yup.boolean(),
      questions: yup.array().of(questionSchema as yup.AnySchema),
    })
  );

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    type: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    notes: yup.string().nullable(),
    haveQuestion: yup.boolean(),
    questions: yup.array().when("haveQuestion", {
      is: true,
      then: yup.array().of(questionSchema).required(translate("thisFieldIsRequired")),
    }),
    appEvents: yup
      .array()
      .min(1, translate("taskTypeIsRequired"))
      .of(
        yup.object().shape({
          type: yup.string().required(translate("thisFieldIsRequired")),
        })
      ),
    tasksType: yup.array().min(1, translate("taskTypeIsRequired")),
    jobsType: yup.array().min(1, translate("orderTypeIsRequired")),
  });

  const setTrueIfArrayIsGraterThanZero = (value?: any[]) => (value ? value.length > 0 : false);

  const handleValidateSchemaSuccess = (schema: yup.ObjectSchema<ObjectShape>): boolean => {
    schema.validateSync(
      {
        code: checklist?.code,
        type: checklist?.type,
        notes: checklist?.notes,
        haveQuestion: setTrueIfArrayIsGraterThanZero(checklist?.questions),
        questions: checklist?.questions,
        appEvents: checklist?.appEvents,
        tasksType: checklist?.tasksType,
        jobsType: checklist?.jobsType,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateSchemaError = (error: yup.ValidationError): boolean => {
    if (error) {
      setErrors(error?.inner);
    }

    return false;
  };

  const handleValidate = (schema: yup.ObjectSchema<ObjectShape>) => {
    try {
      return handleValidateSchemaSuccess(schema);
    } catch (error) {
      if (error instanceof yup.ValidationError) return handleValidateSchemaError(error);

      return false;
    }
  };

  const handleCloseModal = () => {
    handleClearFields();
    closeModal();
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (handleValidate(schema)) {
      submit({ ...checklist });
    }
  };

  useEffect(() => {
    handleUpdateChecklist(checklistToUpdate);
  }, [checklistToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) handleCloseModal();
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      handleValidate(schema);
    }
  }, [hasFormBeenSubmitted, checklist]);

  return (
    <>
      <GeneralTab isOnViewMode={isOnViewMode} />
      <ButtonsContainer>
        <GroupedButtons
          buttons={[
            {
              name: "cancel",
              action: () => handleCloseModal(),
              variant: "text",
              customColor: Color.SEND,
              text: translate("cancel"),
            },
            {
              name: "save",
              action: isOnViewMode ? undefined : () => handleSubmit(),
              disabled: isOnViewMode,
              loading: isLoading,
              customColor: Color.SUCCESS,
              text: checklistToUpdate?.id ? translate("save") : translate("create"),
            },
          ]}
        />
      </ButtonsContainer>
    </>
  );
};

export default memo(ChecklistModal);
